import React, { useEffect, useState } from 'react';
import { AppstoreOutlined, DeleteOutlined } from '@ant-design/icons';
import {
	Alert,
	Button,
	ColorPicker,
	ConfigProvider,
	Flex,
	Input,
	InputNumber,
	Segmented,
	Select,
	Space,
	Tooltip,
	theme as antdTheme,
	type ThemeConfig,
} from 'antd';
import { antdThemeDark, antdThemeLight } from 'admin/components/common/Theme';
import DesignModeControl from 'admin/components/common/DesignModeControl';
import DesignModeValueField from 'admin/components/common/DesignModeValueField';
import Text from 'admin/components/common/Text';
import ResourceAddButton from 'admin/components/common/ResourceEditor/ResourceAddButton';
import ResourceEmptyState from 'admin/components/common/ResourceEditor/ResourceEmptyState';
import ResourceName from 'admin/components/common/ResourceEditor/ResourceName';
import {
	buildFontFamilySelectOptions,
	getUnavailableFontFamilies,
	type FontFamilySelectOption,
	type FontFamilySelectOptionGroup,
} from 'admin/components/common/Form';
import FontFamilyUnavailableOption from 'admin/components/common/Form/FontFamilyUnavailableOption';
import {
	createDesignTokenId,
	createDesignDefinition,
	createDesignModeValue,
	getDefaultDesignModeSelection,
	resolveDesignModeValue,
	validateDesignDefinition,
	type DesignDefinition,
	type DesignModeGroup,
	type DesignToken,
	type DesignTokenSet,
	type DesignTokenType,
	type DesignModeValue,
} from 'common/design-tokens';
import { generateId } from 'utils/generate-id';
import t from 'utils/translate';
import css from './DesignSystemEditor.scss';

export function designDefinitionError(value: unknown): string | undefined {
	try {
		validateDesignDefinition(value ?? createDesignDefinition());
		return undefined;
	} catch (error) {
		return error instanceof Error ? error.message : String(error);
	}
}

type Props = {
	value?: DesignDefinition;
	onChange: (value: DesignDefinition) => void;
	fonts: { fontFamily: string }[];
	fontContext: 'organization' | 'story';
	disabled?: boolean;
	theme?: 'light' | 'dark';
};

type FontFamilyTokenSelectProps = {
	id?: string;
	value: string;
	options: FontFamilySelectOption[];
	unavailableMessage: string;
	disabled?: boolean;
	variant?: 'borderless' | 'outlined';
	ariaDescribedBy?: string;
	onChange: (value: string) => void;
};

function FontFamilyTokenSelect({
	id,
	value,
	options,
	unavailableMessage,
	disabled,
	variant = 'borderless',
	ariaDescribedBy,
	onChange,
}: FontFamilyTokenSelectProps) {
	const select = buildFontFamilySelectOptions(value, options);
	const control = (
		<Select<string, FontFamilySelectOption | FontFamilySelectOptionGroup>
			id={id}
			className={`${css.quietControl} ${select.unavailable ? css.unavailableFont : ''}`}
			aria-label="Font family"
			aria-describedby={ariaDescribedBy}
			variant={variant}
			showSearch
			optionFilterProp="label"
			value={select.value || undefined}
			disabled={disabled}
			placeholder="Select font"
			options={select.options}
			labelRender={({ label }) =>
				select.unavailable ? <FontFamilyUnavailableOption family={value} compact /> : label
			}
			optionRender={option =>
				'unavailable' in option.data && option.data.unavailable ? (
					<FontFamilyUnavailableOption family={String(option.data.value)} />
				) : (
					option.label
				)
			}
			onChange={onChange}
		/>
	);

	return (
		<Tooltip title={select.unavailable ? unavailableMessage : undefined}>
			<span className={`${css.value} ${css.fontFamilyControl}`}>{control}</span>
		</Tooltip>
	);
}

const types: { value: DesignTokenType; label: string }[] = [
	{ value: 'color', label: 'Color' },
	{ value: 'dimension', label: 'Size' },
	{ value: 'number', label: 'Number' },
	{ value: 'fontFamily', label: 'Font family' },
];
const defaultValue = (type: DesignTokenType, fontFamily: string): string | number =>
	({ color: 'rgba(0, 0, 0, 1)', dimension: '0px', number: 0, fontFamily })[type];

const editorThemes: Record<'light' | 'dark', ThemeConfig> = {
	light: {
		...antdThemeLight,
		algorithm: [antdTheme.defaultAlgorithm, antdTheme.compactAlgorithm],
	},
	dark: {
		...antdThemeDark,
		algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm],
	},
};

const DEFAULTS_TAB = 'defaults';

function getInitialEditingModeId(modeGroups: DesignModeGroup[]) {
	const group = modeGroups.find(item => item.id === 'theme') ?? modeGroups[0];
	return group?.defaultModeId;
}

export default function DesignSystemEditor({
	value = createDesignDefinition(),
	onChange,
	fonts,
	fontContext,
	disabled,
	theme = 'light',
}: Props) {
	const error = designDefinitionError(value);
	const { tokens } = value;
	const themeModeGroup = value.modeGroups.find(group => group.id === 'theme');
	const [requestedThemeModeId, setRequestedThemeModeId] = useState(() => getInitialEditingModeId(value.modeGroups));
	const activeThemeModeId = themeModeGroup?.modes.some(mode => mode.id === requestedThemeModeId)
		? requestedThemeModeId
		: themeModeGroup?.defaultModeId;
	const activeThemeModeName = themeModeGroup?.modes.find(mode => mode.id === activeThemeModeId)?.name;
	const activeModeSelection = {
		...getDefaultDesignModeSelection(value.modeGroups),
		...(themeModeGroup && activeThemeModeId ? { [themeModeGroup.id]: activeThemeModeId } : {}),
	};
	const families = [...new Set(fonts.map(font => font.fontFamily))];
	const fontOptions = families.map(family => ({ value: family, label: family }));
	const unavailableTokenFontFamilies = getUnavailableFontFamilies(
		tokens.collections.flatMap(collection =>
			collection.tokens.flatMap(token =>
				token.type === 'fontFamily'
					? [token.value.base, ...(token.value.overrides ?? []).map(override => override.value)]
					: []
			)
		),
		fontOptions
	);
	const unavailableFontMessage =
		fontContext === 'organization'
			? 'This font isn’t available in this organization. Add it in Fonts or choose another font.'
			: 'This font isn’t available in this story. Add it in Story Settings → Fonts or choose another font.';
	const defaultFontUnavailable = buildFontFamilySelectOptions(
		resolveDesignModeValue(value.defaults.root.fontFamily, activeModeSelection),
		fontOptions
	).unavailable;
	const [activeCollectionId, setActiveCollectionId] = useState(DEFAULTS_TAB);
	const activeCollection =
		tokens.collections.find(collection => collection.id === activeCollectionId) ?? tokens.collections[0];

	useEffect(() => {
		if (activeCollectionId !== DEFAULTS_TAB && !tokens.collections.some(item => item.id === activeCollectionId))
			setActiveCollectionId(DEFAULTS_TAB);
	}, [activeCollectionId, tokens.collections]);

	useEffect(() => {
		setRequestedThemeModeId(getInitialEditingModeId(value.modeGroups));
	}, [value.modeGroups]);

	const updateTokens = (next: DesignTokenSet) => onChange({ ...value, tokens: next });

	const update = (collectionId: string, tokenId: string, patch: Partial<DesignToken>) =>
		updateTokens({
			...tokens,
			collections: tokens.collections.map(collection =>
				collection.id !== collectionId
					? collection
					: {
							...collection,
							tokens: collection.tokens.map(token =>
								token.id === tokenId ? ({ ...token, ...patch } as DesignToken) : token
							),
						}
			),
		});
	const removeToken = (collectionId: string, tokenId: string) =>
		updateTokens({
			...tokens,
			collections: tokens.collections.map(collection =>
				collection.id === collectionId
					? { ...collection, tokens: collection.tokens.filter(token => token.id !== tokenId) }
					: collection
			),
		});
	const addToken = (collectionId: string) => {
		const id = createDesignTokenId(
			`token-${generateId(12)}`,
			tokens.collections.flatMap(collection => collection.tokens.map(token => token.id))
		);
		updateTokens({
			...tokens,
			collections: tokens.collections.map(collection =>
				collection.id === collectionId
					? {
							...collection,
							tokens: [
								{
									id,
									name: 'New token',
									type: 'color',
									value: createDesignModeValue('rgba(0, 0, 0, 1)'),
								},
								...collection.tokens,
							],
						}
					: collection
			),
		});
	};
	const addCollection = () => {
		const id = createDesignTokenId(
			`collection-${generateId(12)}`,
			tokens.collections.map(item => item.id)
		);
		updateTokens({
			...tokens,
			collections: [...tokens.collections, { id, name: 'New collection', tokens: [] }],
		});
		setActiveCollectionId(id);
	};

	return (
		<ConfigProvider theme={editorThemes[theme]}>
			<div className={css.editor} data-theme={theme}>
				{error ? <Alert type="error" title={error} /> : null}
				{unavailableTokenFontFamilies.length ? (
					<Alert
						type="warning"
						showIcon
						title={`${unavailableTokenFontFamilies.length} unavailable font${
							unavailableTokenFontFamilies.length === 1 ? '' : 's'
						}: ${unavailableTokenFontFamilies.join(', ')}`}
						description={unavailableFontMessage}
					/>
				) : null}
				<section className={css.modeScope} aria-label="Editing mode">
					{themeModeGroup ? (
						<header className={css.modeHeader}>
							<div className={css.modeCopy}>
								<Text size={Text.size.label} weight={Text.weight.semibold} compact>
									Editing mode
								</Text>
								<Text className={css.modeDescription} size={Text.size.label} compact>
									{activeThemeModeId === themeModeGroup.defaultModeId
										? 'Base values. Other modes inherit them until overridden.'
										: `Values for ${activeThemeModeName}. Editing an inherited value creates an override.`}
								</Text>
							</div>
							<DesignModeControl
								className={css.modeControl}
								value={activeThemeModeId}
								disabled={disabled}
								modes={themeModeGroup.modes}
								onChange={setRequestedThemeModeId}
							/>
						</header>
					) : null}
					<div className={css.modeBody}>
						{tokens.collections.length > 0 && (
							<div className={css.collectionBar}>
								<Segmented
									className={css.collectionTabs}
									aria-label="Token collections"
									value={activeCollectionId}
									disabled={disabled}
									options={[
										{ value: DEFAULTS_TAB, label: 'Defaults' },
										...tokens.collections.map(collection => ({
											value: collection.id,
											label: (
												<span className={css.collectionOption}>
													<span className={css.collectionOptionName} title={collection.name}>
														{collection.name}
													</span>
													<span className={css.collectionOptionCount}>
														{collection.tokens.length}
													</span>
												</span>
											),
										})),
									]}
									onChange={next => setActiveCollectionId(String(next))}
								/>
								<ResourceAddButton theme={theme} disabled={disabled} onClick={addCollection}>
									{t('designTokens.newCollection')}
								</ResourceAddButton>
							</div>
						)}
						{activeCollectionId === DEFAULTS_TAB ? (
							<>
								<section className={css.defaults}>
									<Flex
										className={css.defaultPanel}
										gap="large"
										justify="space-between"
										align="flex-start"
										wrap
									>
										<Flex className={css.defaultCopy} vertical gap={4}>
											<Text
												tag="label"
												htmlFor="design-default-font-family"
												size={Text.size.body}
												weight={Text.weight.semibold}
												compact
											>
												Default font family
											</Text>
											<Text className={css.defaultDescription} size={Text.size.label} compact>
												Used for text that doesn&apos;t have its own font.
											</Text>
										</Flex>
										<Flex className={css.defaultControl} vertical gap={6}>
											<DesignModeValueField
												value={value.defaults.root.fontFamily}
												selection={activeModeSelection}
												groups={value.modeGroups}
												disabled={disabled}
												onChange={fontFamily =>
													onChange({
														...value,
														defaults: {
															...value.defaults,
															root: { ...value.defaults.root, fontFamily },
														},
													})
												}
											>
												{({ value: fontFamily, setValue }) => (
													<FontFamilyTokenSelect
														id="design-default-font-family"
														value={fontFamily}
														options={fontOptions}
														unavailableMessage={unavailableFontMessage}
														disabled={disabled}
														variant="outlined"
														ariaDescribedBy={
															defaultFontUnavailable
																? 'design-default-font-unavailable'
																: undefined
														}
														onChange={setValue}
													/>
												)}
											</DesignModeValueField>
											{defaultFontUnavailable ? (
												<Text
													id="design-default-font-unavailable"
													className={css.defaultError}
													size={Text.size.description}
													compact
												>
													{unavailableFontMessage}
												</Text>
											) : null}
										</Flex>
									</Flex>
								</section>
								{!tokens.collections.length && (
									<ResourceEmptyState
										inset
										title="No token collections yet"
										description="Create a collection to organize reusable colors, sizes, numbers and fonts."
										icon={<AppstoreOutlined />}
										action={
											<ResourceAddButton
												prominent
												theme={theme}
												disabled={disabled}
												onClick={addCollection}
											>
												{t('designTokens.newCollection')}
											</ResourceAddButton>
										}
									/>
								)}
							</>
						) : activeCollection ? (
							<section className={css.collection}>
								<Flex justify="space-between" align="center" className={css.collectionHeader}>
									<strong className={css.collectionName}>
										<ResourceName
											key={activeCollection.id}
											label="Collection"
											name={activeCollection.name}
											disabled={disabled}
											onRename={name =>
												updateTokens({
													...tokens,
													collections: tokens.collections.map(item =>
														item.id === activeCollection.id ? { ...item, name } : item
													),
												})
											}
										/>
									</strong>
									{activeCollection.tokens.length > 0 && (
										<ResourceAddButton
											theme={theme}
											disabled={disabled}
											onClick={() => addToken(activeCollection.id)}
										>
											{t('designTokens.newToken')}
										</ResourceAddButton>
									)}
								</Flex>
								{activeCollection.tokens.length ? (
									<div className={css.tokenList}>
										<div className={css.tokenHeader} aria-hidden="true">
											<span>Type</span>
											<span>Name</span>
											<span>Value</span>
											<span>Description</span>
											<span />
										</div>
										{activeCollection.tokens.map(token => (
											<div key={token.id} className={css.token}>
												<Select
													className={`${css.tokenType} ${css.quietControl}`}
													aria-label="Token type"
													variant="borderless"
													value={token.type}
													options={types}
													disabled={disabled}
													onChange={(type: DesignTokenType) =>
														update(activeCollection.id, token.id, {
															type,
															value: createDesignModeValue(
																defaultValue(type, value.defaults.root.fontFamily.base)
															),
														} as Partial<DesignToken>)
													}
												/>
												<Input
													className={`${css.tokenName} ${css.quietControl}`}
													aria-label="Token name"
													variant="borderless"
													value={token.name}
													disabled={disabled}
													onChange={event =>
														update(activeCollection.id, token.id, {
															name: event.target.value,
														})
													}
												/>
												<DesignModeValueField<string | number>
													value={token.value as DesignModeValue<string | number>}
													selection={activeModeSelection}
													groups={value.modeGroups}
													disabled={disabled}
													onChange={next =>
														update(activeCollection.id, token.id, {
															value: next,
														} as Partial<DesignToken>)
													}
												>
													{({ value: tokenValue, setValue }) =>
														token.type === 'fontFamily' ? (
															<FontFamilyTokenSelect
																value={String(tokenValue)}
																options={fontOptions}
																unavailableMessage={unavailableFontMessage}
																disabled={disabled}
																onChange={setValue}
															/>
														) : token.type === 'number' ? (
															<InputNumber
																className={`${css.value} ${css.quietControl}`}
																aria-label="Number value"
																variant="borderless"
																value={
																	Number.isFinite(tokenValue)
																		? Number(tokenValue)
																		: null
																}
																disabled={disabled}
																onChange={next => setValue(next ?? NaN)}
															/>
														) : token.type === 'dimension' ? (
															<InputNumber<string>
																className={`${css.value} ${css.quietControl}`}
																aria-label="Token value"
																variant="borderless"
																stringMode
																controls={false}
																suffix="px"
																value={
																	typeof tokenValue === 'string' &&
																	/px$/i.test(tokenValue)
																		? tokenValue.slice(0, -2)
																		: null
																}
																disabled={disabled}
																onChange={next =>
																	setValue(next === null ? '' : `${next}px`)
																}
															/>
														) : (
															<Space.Compact className={css.value}>
																{token.type === 'color' ? (
																	<ColorPicker
																		size="small"
																		value={String(tokenValue)}
																		disabled={disabled}
																		styles={{
																			root: {
																				padding: 0,
																				overflow: 'hidden',
																				alignItems: 'stretch',
																				flex: '0 0 28px',
																				background: 'transparent',
																				borderColor: 'transparent',
																			},
																			body: {
																				width: '100%',
																				height: '100%',
																				borderRadius: 4,
																				boxShadow: 'none',
																				border: '1px solid var(--ra-color-gray-20)',
																			},
																			content: { boxShadow: 'none' },
																		}}
																		onChangeComplete={color =>
																			setValue(color.toRgbString())
																		}
																	/>
																) : null}
																<Input
																	className={css.quietControl}
																	variant="borderless"
																	aria-label="Token value"
																	value={String(tokenValue)}
																	disabled={disabled}
																	onChange={event => setValue(event.target.value)}
																/>
															</Space.Compact>
														)
													}
												</DesignModeValueField>
												<Input.TextArea
													className={`${css.description} ${css.quietControl}`}
													aria-label="Token description"
													variant="borderless"
													autoSize={{ minRows: 1, maxRows: 3 }}
													placeholder="Optional"
													value={token.description ?? ''}
													disabled={disabled}
													onChange={event =>
														update(activeCollection.id, token.id, {
															description: event.target.value,
														})
													}
												/>
												<Tooltip title={t('designTokens.delete')}>
													<Button
														className={css.deleteToken}
														aria-label={t('designTokens.delete')}
														danger
														type="text"
														size="small"
														icon={<DeleteOutlined />}
														disabled={disabled}
														onClick={() => removeToken(activeCollection.id, token.id)}
													/>
												</Tooltip>
											</div>
										))}
									</div>
								) : (
									<ResourceEmptyState
										inset
										title="No tokens yet"
										description="Add a reusable color, size, number or font family to this collection."
										action={
											<ResourceAddButton
												prominent
												theme={theme}
												disabled={disabled}
												onClick={() => addToken(activeCollection.id)}
											>
												{t('designTokens.newToken')}
											</ResourceAddButton>
										}
									/>
								)}
							</section>
						) : null}
					</div>
				</section>
			</div>
		</ConfigProvider>
	);
}
